import { apiDelete, apiFetch, apiPatch, apiPost, apiPut } from "./api";

export interface ScopeRule {
  id?: number;
  queue_id: number | null;
  include_subqueues: boolean;
  customer: string | null;
  date_from: string | null;
  date_to: string | null;
}

export interface PermissionGroup {
  code: string;
  permissions: string[];
}

export interface AdminQueue {
  id: number;
  full_name: string;
  name: string;
  parent_id: number | null;
  depth: number;
  is_valid: boolean;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  is_system: boolean;
  require_2fa: boolean;
  permissions: string[];
  scope_rules: ScopeRule[];
  user_count: number;
}

export interface RoleInput {
  name: string;
  description: string;
  require_2fa: boolean;
  permissions: string[];
  scope_rules: ScopeRule[];
}

export interface RoleRef {
  id: number;
  name: string;
  is_system: boolean;
}

export interface AdminUser {
  id: number;
  username: string;
  display_name: string;
  email: string | null;
  is_active: boolean;
  must_change_password: boolean;
  totp_enabled: boolean;
  locked: boolean;
  last_login_at: string | null;
  roles: RoleRef[];
}

export interface AdminUserDetail extends AdminUser {
  overrides: Record<string, boolean>;
  scope_rules: ScopeRule[];
  effective_permissions: string[];
}

export interface UserUpdate {
  display_name?: string;
  email?: string | null;
  is_active?: boolean;
  role_ids?: number[];
  overrides?: Record<string, boolean>;
  scope_rules?: ScopeRule[];
}

export interface AuditEntry {
  id: number;
  at: string;
  user_id: number | null;
  username: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  ip: string | null;
  details: Record<string, unknown> | null;
}

export const adminApi = {
  deleteUser: (id: number) => apiDelete(`/admin/users/${id}`),
  viewAs: (id: number) => apiPost<void>(`/admin/users/${id}/view-as`),
  permissions: () => apiFetch<PermissionGroup[]>("/admin/permissions"),
  queues: () => apiFetch<AdminQueue[]>("/admin/queues"),
  roles: () => apiFetch<Role[]>("/admin/roles"),
  createRole: (body: RoleInput) => apiPost<Role>("/admin/roles", body),
  updateRole: (id: number, body: RoleInput) => apiPut<Role>(`/admin/roles/${id}`, body),
  deleteRole: (id: number) => apiDelete(`/admin/roles/${id}`),
  users: () => apiFetch<AdminUser[]>("/admin/users"),
  user: (id: number) => apiFetch<AdminUserDetail>(`/admin/users/${id}`),
  createUser: (body: { username: string; display_name: string; email: string | null; role_ids: number[] }) =>
    apiPost<{ user: AdminUserDetail; temporary_password: string }>("/admin/users", body),
  updateUser: (id: number, body: UserUpdate) => apiPatch<AdminUserDetail>(`/admin/users/${id}`, body),
  resetPassword: (id: number) => apiPost<{ temporary_password: string }>(`/admin/users/${id}/reset-password`),
  reset2fa: (id: number) => apiPost<void>(`/admin/users/${id}/reset-2fa`),
  unlock: (id: number) => apiPost<void>(`/admin/users/${id}/unlock`),
  audit: (params: URLSearchParams) =>
    apiFetch<{ items: AuditEntry[]; total: number }>(`/admin/audit?${params.toString()}`),
};

export function roleInput(role: Role): RoleInput {
  return {
    name: role.name,
    description: role.description,
    require_2fa: role.require_2fa,
    permissions: role.permissions,
    scope_rules: role.scope_rules,
  };
}
